import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Echo from "./Echo";
import logger from "../../Utils/logger";

const EVENT_BARE = "outlet.access.unauthorized";
const EVENT_DOTTED = `.${EVENT_BARE}`;

/**
 * Global listener for outlet.access.unauthorized on private user.{id}.
 * Reloads the app so outlet access / permissions refresh.
 * Does not Echo.leave the shared user.* channel (payroll/reports also use it).
 */
const OutletAccessUnauthorizedListener = () => {
	const location = useLocation();
	const currentChannelRef = useRef(null);
	const boundHandlerRef = useRef(null);
	const isReloadingRef = useRef(false);

	const detachListener = useCallback(() => {
		const channel = currentChannelRef.current;
		const handler = boundHandlerRef.current;

		try {
			channel?.stopListening?.(EVENT_BARE);
			channel?.stopListening?.(EVENT_DOTTED);
		} catch (e) {}

		try {
			const userId = localStorage.getItem("user_id");
			if (userId && handler) {
				const pusherChannel = Echo?.connector?.pusher?.channel?.(
					`private-user.${userId}`
				);
				pusherChannel?.unbind?.(EVENT_BARE, handler);
			}
		} catch (e) {}

		boundHandlerRef.current = null;
	}, []);

	const subscribe = useCallback(() => {
		const userId = localStorage.getItem("user_id");
		const token = localStorage.getItem("token");

		if (!userId || !token) {
			detachListener();
			currentChannelRef.current = null;
			return;
		}

		const channelName = `user.${userId}`;
		let channel;
		try {
			detachListener();
			channel = Echo.private(channelName);
			currentChannelRef.current = channel;
		} catch (e) {
			return;
		}

		const onEvent = (event) => {
			if (isReloadingRef.current) return;
			isReloadingRef.current = true;
			logger.log("socket outlet.access.unauthorized received", event);
			window.location.reload();
		};

		boundHandlerRef.current = onEvent;

		try {
			channel.listen(EVENT_DOTTED, onEvent);
		} catch (e) {}

		try {
			const pusherChannel = Echo?.connector?.pusher?.channel?.(
				`private-${channelName}`
			);
			pusherChannel?.bind?.(EVENT_BARE, onEvent);
		} catch (e) {}
	}, [detachListener]);

	useEffect(() => {
		subscribe();
	}, [subscribe, location.pathname]);

	useEffect(() => {
		const onReconnected = () => subscribe();
		window.addEventListener("socket:reconnected", onReconnected);
		return () => window.removeEventListener("socket:reconnected", onReconnected);
	}, [subscribe]);

	useEffect(() => () => detachListener(), [detachListener]);

	return null;
};

export default OutletAccessUnauthorizedListener;
